import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone, MapPin } from 'lucide-react';
import { GLSLHills } from './GLSLHills';

// Instagram ve YouTube SVG ikonları
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const footerLinks = [
  {
    title: 'Büyüme Sistemleri',
    links: [
      { name: 'Dijital Deneyim & Algı Mimarisi', href: '/buyume-sistemleri/dijital-deneyim-algi-mimarisi' },
      { name: 'Otorite & Talep Motoru', href: '/buyume-sistemleri/otorite-talep-motoru' },
      { name: 'Performans & İkna Hızlandırıcı', href: '/buyume-sistemleri/performans-ikna-hizlandirici' },
    ],
  },
  {
    title: 'Platform',
    links: [
      { name: 'aifabric', href: '/aifabric' },
      { name: 'Metodoloji', href: '/metodoloji' },
      { name: 'Portföy', href: '/portfolyo' },
    ],
  },
  {
    title: 'İletişim',
    links: [
      { name: 'Büyüme Analizi', href: '/iletisim' },
      { name: 'WhatsApp', href: 'https://wa.me/905321686282', external: true },
    ],
  },
];

export const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A]" data-testid="footer">
      {/* Main Footer Content */}
      <div className="container-wide section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/images/brand/medyatik-logo-beyaz.png" 
                alt="Medyatik" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              Strateji, teknoloji ve yapay zekâ ile ölçeklenebilir büyüme sistemleri tasarlıyoruz.
            </p>
            <div className="flex flex-col gap-3 text-sm text-zinc-500">
              <a 
                href="mailto:info@medyatik.app" 
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                info@medyatik.app
              </a>
              <a 
                href="tel:+905321686282" 
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                +90 532 168 62 82
              </a>
              <a 
                href="https://maps.google.com/?q=Esentepe+Mahallesi+Talatpaşa+Caddesi+No:5/1+Şişli+İstanbul"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-white transition-colors"
              >
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  Esentepe Mah. Talatpaşa Cad.<br />
                  No: 5/1, 34394 Şişli/İstanbul
                </span>
              </a>
            </div>

            {/* Social Media Icons */}
            <div className="flex items-center gap-4 mt-6">
              <a 
                href="https://www.instagram.com/medyatikinteractive/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center text-zinc-400 hover:text-white hover:border-pink-500/50 hover:bg-pink-500/10 transition-all"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <InstagramIcon className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@medyatikinteractive8881"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center text-zinc-400 hover:text-white hover:border-red-500/50 hover:bg-red-500/10 transition-all"
                aria-label="YouTube"
                data-testid="social-youtube"
              >
                <YouTubeIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Link Columns */}
          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="text-white font-semibold mb-6 text-sm tracking-wide uppercase">
                {column.title}
              </h4>
              <ul className="space-y-4">
                {column.links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                      >
                        {link.name}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-zinc-400 hover:text-white transition-colors text-sm"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* GLSL Hills 3D Wave */}
      <div className="-mt-8">
        <div className="relative overflow-hidden" style={{ height: '280px' }}>
          <GLSLHills height="280px" cameraZ={110} speed={0.3} />
          {/* Overlay gradient edges */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/80 pointer-events-none z-[2]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]/60 pointer-events-none z-[2]" />
        </div>
      </div>

      {/* Copyright */}
      <div>
        <div className="container-wide py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-zinc-500">
            <p>2006-2026 | Medyatik Interactive Tüm Hakları Saklıdır</p>
            <div className="flex items-center gap-6">
              <Link to="/kvkk" className="hover:text-white transition-colors">
                KVKK
              </Link>
              <Link to="/gizlilik" className="hover:text-white transition-colors">
                Gizlilik Politikası
              </Link>
              <Link to="/kullanim-sartlari" className="hover:text-white transition-colors">
                Kullanım Şartları
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
